<template>
  <v-card style="min-height:400px">
    <v-row>
      <v-col :cols="12">
        <v-card elevation="3" style="margin:3px">
          <v-subheader>Course Listings</v-subheader>
          <v-divider></v-divider>
          <v-tabs v-model="searchTab">
            <v-tab>Recommended</v-tab>
            <v-tab>Search</v-tab>
          </v-tabs>
          <v-tabs-items v-model="searchTab">
            <v-tab-item>
              <recommended-courses :major="major" :schedule="schedule" :course-search-filter="courseSearchFilter" @addCourse="(course) => schedule.push(course)"></recommended-courses>
            </v-tab-item>
            <v-tab-item>
              <search-courses :schedule="schedule" :course-search-filter="courseSearchFilter" @addCourse="(course) => schedule.push(course)"></search-courses>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col :cols="12">
        <v-card elevation="3" style="margin:3px;">
          <v-subheader>Selected Courses</v-subheader>
          <v-divider></v-divider>
          <v-tabs v-model="scheduleTab">
            <v-tab>List View</v-tab>
            <v-tab>Calendar View</v-tab>
          </v-tabs>
          <v-tabs-items v-model="scheduleTab">
            <v-tab-item>
              <v-list v-if="schedule.length > 0">
                <template v-for="(course, index) in schedule">
                  <v-list-item :key="'course-' + index">
                    <v-list-item-content>
                      <v-list-item-title style="white-space:normal">{{ course.main_label }}</v-list-item-title>
                      <v-list-item-subtitle>Meets: {{ course.time_label }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="course.course_num !== 'COR100'">
                      <v-icon color="error" @click="schedule.splice(index, 1)">fal fa-times-circle</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider v-if="index < schedule.length" :key="'div-' + index"></v-divider>
                </template>
              </v-list>
            </v-tab-item>
            <v-tab-item>
              <v-calendar ref="calendar" :weekdays="weekdays" :start="startDate" :events="events" type="week" style="max-height:600px" @click:event="showCourse"></v-calendar>
              <v-menu v-model="courseDetailOpen" :close-on-content-click="false" :activator="selectedElement" offset-x>
                <v-card color="grey lighten-4" min-width="350px" flat>
                  <v-toolbar color="info" dark>
                    <v-toolbar-title v-html="selectedCourse.name"></v-toolbar-title>
                  </v-toolbar>
                  <v-card-text>
                    <div>Section: {{ selectedCourse.section }}</div>
                    <div>Date/Time: {{ selectedCourse.timeLabel }}</div>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn color="secondary" text @click="courseDetailOpen = false">Close</v-btn>
                    <v-btn v-if="selectedCourse.courseNum !== 'COR100'" color="error" text @click="removeCourse(selectedCourse)">Remove Course</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-tab-item>
          </v-tabs-items>
          <v-card-text>Selected Credit Hours: {{ hours }}</v-card-text>
          <v-alert v-if="hours < 12" type="error">You must select at least 12 credit hours of classes before you can continue.</v-alert>
          <v-alert v-else-if="hours > 18" type="error">The maximum hours you can select is 18. Please remove classes from your schedule until you are at or under 18 before you can continue.</v-alert>
        </v-card>
      </v-col>
    </v-row>
    <v-card-actions>
      <v-btn :disabled="hours < 12 || hours > 18" color="primary" @click="next()">Continue</v-btn>
      <v-btn @click="$emit('back')">Back</v-btn>
    </v-card-actions>
  </v-card>
</template>
<style>
.v-calendar-daily_head-day-label {
  display: none;
}
.v-calendar-daily_head-weekday {
  margin: .7em auto;
}
</style>
<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api'
export default {
  components: {
    RecommendedCourses: () => import('./courses/recommended'),
    SearchCourses: () => import('./courses/search')
  },
  props: {
    major: {
      type: String,
      required: true
    }
  },
  setup (props, { root, emit }) {
    const searchTab = ref(0)
    const scheduleTab = ref(1)
    const schedule = ref([])
    const courseSearchFilter = ref(['MSP143'])

    const calendar = ref(null)
    const startDate = computed(() => {
      let term = root.$store.state.term
      let year = term.substr(0, 4)
      let date = new Date(year + '-09-01')
      if (term.substr(4, 2) === '05') {
        date = new Date((parseInt(year) + 1) + '-01-01')
      }
      date.setDate(date.getDate() + 8 - date.getDay())
      return date.toISOString().substr(0, 10)
    })
    const weekdays = computed(() => {
      for (let i = 0; i < schedule.value.length; i++) {
        if (schedule.value[i].day_time.indexOf('S') >= 0) {
          return [1, 2, 3, 4, 5, 6]
        }
      }
      return [1, 2, 3, 4, 5]
    })

    const events = computed(() => {
      let dates = { M: startDate.value, T: '', W: '', R: '', F: '', S: '' }
      let dt = new Date(startDate.value)
      dt.setDate(dt.getDate() + 1)
      dates.T = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.W = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.R = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.F = dt.toISOString(0, 10)
      dt.setDate(dt.getDate() + 1)
      dates.S = dt.toISOString(0, 10)
      let arr = []
      for (const { course_num: courseNum, title, day_time: dayTime, value: crn, section, time_label: timeLabel } of schedule.value) {
        let dayEntries = dayTime.split(';')
        for (let i = 0; i < dayEntries.length; i++) {
          let [days, time] = dayEntries[i].split(' ')
          for (let j = 0; j < days.length; j++) {
            if (days[j] in dates) {
              let obj = { name: courseNum + ' ' + title, timed: true, courseNum, crn, section, timeLabel }
              obj.start = new Date(dates[days[j]])
              obj.start.setHours(time.substr(0, 2))
              obj.start.setMinutes(time.substr(2, 2))
              obj.end = new Date(dates[days[j]])
              obj.end.setHours(time.substr(5, 2))
              obj.end.setMinutes(time.substr(7, 2))
              arr.push(obj)
            }
          }
        }
      }
      return arr
    })
    const hours = computed(() => {
      let hours = 0
      for (const { credits } of schedule.value) hours += credits
      return hours
    })

    watch(calendar, () => {
      if (calendar.value) calendar.value.scrollToTime('08:00')
    })

    onMounted(() => {
      root.$nextTick(() => { scheduleTab.value = 0 })
    })

    const courseDetailOpen = ref(false)
    const selectedElement = ref(null)
    const selectedCourse = ref({})
    function showCourse ({ nativeEvent, event }) {
      const open = () => {
        selectedCourse.value = event
        selectedElement.value = nativeEvent.target
        setTimeout(() => {
          courseDetailOpen.value = true
        }, 10)
      }

      if (courseDetailOpen.value) {
        courseDetailOpen.value = false
        setTimeout(open, 10)
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    }

    function removeCourse (course) {
      for (let i = 0; i < schedule.value.length; i++) {
        if (schedule.value[i].value === course.crn) {
          schedule.value.splice(i, 1)
          courseDetailOpen.value = false
          selectedElement.value = null
          selectedCourse.value = {}
          return
        }
      }
    }

    function next () {
      if (hours.value >= 12 && hours.value <= 18) {
        const courses = []
        for (const { value } of schedule.value) courses.push(value)
        emit('next', { courses })
      }
    }

    let query = { term_code: root.$store.state.term, course_num: 'COR100', section: 1, $limit: 5 }
    root.$feathers.service('warehouse/courses').find({ query }).then(({ data }) => {
      if (data.length > 0) schedule.value.push(data[0])
    })

    return {
      searchTab,
      scheduleTab,
      schedule,
      courseSearchFilter,
      calendar,
      startDate,
      weekdays,
      events,
      hours,
      courseDetailOpen,
      selectedElement,
      selectedCourse,
      showCourse,
      removeCourse,
      next
    }
  }
}
</script>
